import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { isFlightDetailsComplete } from '../utils/flightValidation';
import { isQuoteSettingsComplete } from '../utils/quoteValidation';

/**
 * Custom hook for managing quote form state
 * Handles all form data, validation, and section management
 *
 * @returns {Object} Form state and handlers
 */
const useQuoteForm = () => {
  // Form data state
  const [formData, setFormData] = useState({
    // Client section
    client: {
      type: 'new',
      clientData: {}
    },

    // Flight section
    flights: {
      type: 'one-way',
      flights: [
        {
          departure: null,
          arrival: null,
          departureDate: '',
          returnDate: '',
          passengerCount: 1,
          notes: ''
        }
      ]
    },

    // Aircraft section
    aircraft: null,

    bidOptions: null,

    // Quote settings
    settings: {
      bidTitle: '',
      validUntil: '',
      expirationDate: '',
      notes: '',
      operator_price: '',
      broker_markup: '',
      final_price: ''
    }
  });

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    client: true,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Track which sections have been unlocked (by clicking Next)
  const [unlockedSections, setUnlockedSections] = useState({
    client: true,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Track sections marked complete by clicking Next
  const [manuallyCompleted, setManuallyCompleted] = useState({
    client: false,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Calculate completed sections (form validation OR manually marked via Next)
  const completedSections = useMemo(() => {
    const clientComplete = isClientComplete(formData.client) || manuallyCompleted.client;
    const flightsComplete = isFlightDetailsComplete(formData.flights) || manuallyCompleted.flights;
    const aircraftComplete = !!formData.aircraft || manuallyCompleted.aircraft;
    const settingsComplete = isQuoteSettingsComplete(formData.settings) || manuallyCompleted.settings;

    return {
      client: clientComplete,
      flights: flightsComplete,
      aircraft: aircraftComplete,
      settings: settingsComplete
    };
  }, [formData, manuallyCompleted]);

  // Auto-expand next section when current is complete (DISABLED - using manual Next button instead)
  // useEffect(() => {
  //   if (completedSections.client && !expandedSections.flights && !completedSections.flights) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       client: false,
  //       flights: true
  //     }));
  //   } else if (completedSections.flights && !expandedSections.aircraft && !completedSections.aircraft) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       flights: false,
  //       aircraft: true
  //     }));
  //   } else if (completedSections.aircraft && !expandedSections.settings && !completedSections.settings) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       aircraft: false,
  //       settings: true
  //     }));
  //   }
  // }, [completedSections, expandedSections]);

  // Track last auto-generated title so we can regenerate when flights change
  const autoTitleRef = useRef('');

  // Auto-generate bid title from flight data
  useEffect(() => {
    if (formData.flights?.flights?.length > 0) {
      const flights = formData.flights.flights;
      const firstFlight = flights[0];
      const departure = firstFlight.departure?.icao;
      const arrival = firstFlight.arrival?.icao;

      if (departure && arrival) {
        const flightType = formData.flights.type;
        let title = '';

        if (flightType === 'round-trip') {
          const legs = [departure, ...flights.map(f => f.arrival?.icao).filter(Boolean)];
          title = `Trip ${legs.join(' → ')}`;
        } else if (flightType === 'multi-leg') {
          const legs = [departure, ...flights.map(f => f.arrival?.icao).filter(Boolean)];
          title = `Trip ${legs.join(' → ')}`;
        } else {
          title = `${departure} → ${arrival}`;
        }

        // Only update if title is empty or matches the previous auto-generated title
        const currentTitle = formData.settings.bidTitle;
        if (!currentTitle || currentTitle === autoTitleRef.current) {
          autoTitleRef.current = title;
          setFormData(prev => ({
            ...prev,
            settings: {
              ...prev.settings,
              bidTitle: title
            }
          }));
        }
      }
    }
  }, [formData.flights]);

  // Update client section
  const updateClient = useCallback((clientData) => {
    setFormData(prev => ({
      ...prev,
      client: clientData
    }));
  }, []);

  // Update flights section
  const updateFlights = useCallback((flightsData) => {
    setFormData(prev => ({
      ...prev,
      flights: flightsData
    }));
  }, []);

  // Update aircraft selection
  const updateAircraft = useCallback((aircraft) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        aircraft: aircraft
      };
      console.log('New formData:', newData);
      return newData;
    });
  }, []);

  // Update settings section
  const updateSettings = useCallback((settings) => {
    setFormData(prev => ({
      ...prev,
      settings: settings
    }));
  }, []);

  // Update entire bidOptions array (for initialization)
  const updateBidOptions = useCallback((options) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: options
    }));
  }, []);

  // Update a specific property for a specific bid option by its ID
  const updateBidOptionProperty = useCallback((optionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: prev.bidOptions?.map(option =>
        option.bid_option_id === optionId
          ? { ...option, [field]: value }
          : option
      ) || []
    }));
  }, []);

  // Update multiple properties for a specific bid option by its ID
  const updateBidOptionProperties = useCallback((optionId, updates) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: prev.bidOptions?.map(option =>
        option.bid_option_id === optionId
          ? { ...option, ...updates }
          : option
      ) || []
    }));
  }, []);

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => {
      const isExpanding = !prev[section];
      if (isExpanding) {
        // Collapse all others, expand only the clicked one
        const newState = {};
        Object.keys(prev).forEach(key => { newState[key] = false; });
        newState[section] = true;
        return newState;
      }
      return { ...prev, [section]: false };
    });
  }, []);

  // Go to next section (collapse current, expand next, unlock next)
  const goToNextSection = useCallback((currentSection) => {
    const sectionOrder = ['client', 'flights', 'aircraft', 'settings'];
    const currentIndex = sectionOrder.indexOf(currentSection);

    if (currentIndex >= 0 && currentIndex < sectionOrder.length - 1) {
      const nextSection = sectionOrder[currentIndex + 1];
      setExpandedSections(prev => ({
        ...prev,
        [currentSection]: false,
        [nextSection]: true
      }));
      // Mark current section as complete
      setManuallyCompleted(prev => ({
        ...prev,
        [currentSection]: true
      }));
      // Unlock the next section
      setUnlockedSections(prev => ({
        ...prev,
        [nextSection]: true
      }));
    }
  }, []);

  // Go to previous section (collapse current, expand previous)
  const goToPreviousSection = useCallback((currentSection) => {
    const sectionOrder = ['client', 'flights', 'aircraft', 'settings'];
    const currentIndex = sectionOrder.indexOf(currentSection);
    
    if (currentIndex > 0) {
      const previousSection = sectionOrder[currentIndex - 1];
      setExpandedSections(prev => ({
        ...prev,
        [currentSection]: false,
        [previousSection]: true
      }));
    }
  }, []);

  // Check if entire form is complete (aircraft section is view-only, not required)
  const isFormComplete = useMemo(() => {
    const complete = (
      completedSections.client &&
      completedSections.flights &&
      completedSections.settings
    );
    return complete;
  }, [completedSections]);

  // Reset form
  const resetForm = useCallback(({ preserveBidOptions } = {}) => {
    setFormData(prev => ({
      client: {
        type: 'new',
        clientData: {}
      },
      flights: {
        type: 'one-way',
        flights: [
          {
            departure: null,
            arrival: null,
            departureDate: '',
            returnDate: '',
            passengerCount: 1,
            notes: ''
          }
        ]
      },
      aircraft: null,
      bidOptions: preserveBidOptions ? prev.bidOptions : null,
      settings: {
        bidTitle: '',
        validUntil: '',
        expirationDate: '',
        notes: ''
      }
    }));
    setExpandedSections({
      client: true,
      flights: false,
      aircraft: false,
      settings: false
    });
    setUnlockedSections({
      client: true,
      flights: false,
      aircraft: false,
      settings: false
    });
    setManuallyCompleted({
      client: false,
      flights: false,
      aircraft: false,
      settings: false
    });
  }, []);

  // Get form errors (aircraft section is view-only, not validated)
  const getFormErrors = useCallback(() => {
    const errors = {};

    if (!completedSections.client) {
      errors.client = 'Client information is incomplete';
    }

    if (!completedSections.flights) {
      errors.flights = 'Flight details are incomplete';
    }

    return errors;
  }, [completedSections]);

  return {
    formData,
    updateClient,
    updateFlights,
    updateAircraft,
    updateSettings,
    updateBidOptions,
    updateBidOptionProperty,
    updateBidOptionProperties,
    toggleSection,
    goToNextSection,
    goToPreviousSection,
    expandedSections,
    unlockedSections,
    completedSections,
    isFormComplete,
    resetForm,
    getFormErrors
  };
};

/**
 * Validates if client information is complete
 * @param {Object} client - Client data object
 * @returns {boolean}
 */
const isClientComplete = (client) => {
  if (!client || !client.clientData) {
    return false;
  }

  const data = client.clientData;

  // For existing client
  if (client.type === 'existing') {
    const email = data.email;
    const firstName = data.first_name || data.firstName;
    const lastName = data.last_name || data.lastName;
    return !!(email && firstName && lastName);
  }

  // For new client
  if (client.type === 'new') {
    return !!(
      data.firstName &&
      data.firstName.trim() !== '' &&
      data.lastName &&
      data.lastName.trim() !== '' &&
      data.email &&
      data.email.trim() !== '' &&
      isValidEmail(data.email)
    );
  }

  return false;
};

/**
 * Basic email validation
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export default useQuoteForm;
